<template>
    <v-container class="download-app mt-10 pb-12">

        <!-- Hero -->
        <div class="hero pa-8 mb-8">
            <div class="hero-icon">
                <v-icon size="40" color="white">mdi-download-circle-outline</v-icon>
            </div>
            <h1 class="text-h4 font-weight-bold white--text mb-2">Download the DGTasks App</h1>
            <p class="text-subtitle-1 mb-0 hero-sub">
                Install the desktop app to track your time and keep your day in sync with DGTasks.
                You always get the latest version, and no GitHub account is needed.
            </p>
        </div>

        <!-- Platform cards -->
        <v-row>
            <v-col v-for="platform in platforms" :key="platform.key" cols="12" md="6">
                <v-card class="platform-card pa-6 fill-height d-flex flex-column"
                    :class="{ recommended: detectedPlatform === platform.os }" outlined>
                    <v-chip v-if="detectedPlatform === platform.os" x-small color="primary" label
                        class="recommended-chip font-weight-bold">
                        <v-icon left x-small>mdi-check-circle</v-icon>
                        Recommended for you
                    </v-chip>

                    <div class="d-flex align-center mb-5">
                        <div class="platform-icon mr-4" :style="{ backgroundColor: platform.color + '1a' }">
                            <v-icon size="34" :color="platform.color">{{ platform.icon }}</v-icon>
                        </div>
                        <div>
                            <div class="text-h6 font-weight-bold">{{ platform.title }}</div>
                            <div class="text-body-2 grey--text">{{ platform.subtitle }}</div>
                        </div>
                    </div>

                    <div class="mt-auto">
                        <v-btn v-for="(download, i) in platform.downloads" :key="download.path"
                            :href="downloadUrl(download.path)" :color="i === 0 ? 'primary' : undefined"
                            :outlined="i !== 0" :depressed="i === 0" block large class="text-none font-weight-medium"
                            :class="{ 'mb-3': i < platform.downloads.length - 1 }">
                            <v-icon left>mdi-download</v-icon>
                            {{ download.label }}
                        </v-btn>

                        <div v-if="platform.hint" class="hint d-flex mt-4 pa-3">
                            <v-icon small class="mr-2 mt-1" color="grey">mdi-information-outline</v-icon>
                            <span class="text-caption grey--text text--darken-1">{{ platform.hint }}</span>
                        </div>
                    </div>
                </v-card>
            </v-col>
        </v-row>

        <!-- Install steps -->
        <v-card outlined class="pa-6 mt-8">
            <div class="text-h6 font-weight-bold mb-4">Getting started</div>
            <v-row>
                <v-col v-for="(step, i) in steps" :key="step.title" cols="12" md="4">
                    <div class="d-flex">
                        <div class="step-number mr-3">{{ i + 1 }}</div>
                        <div>
                            <div class="font-weight-bold mb-1">{{ step.title }}</div>
                            <div class="text-body-2 grey--text text--darken-1">{{ step.text }}</div>
                        </div>
                    </div>
                </v-col>
            </v-row>
        </v-card>
    </v-container>
</template>

<script>
export default {
    name: 'DownloadAppComponent',
    data() {
        return {
            platforms: [
                {
                    key: 'windows',
                    os: 'windows',
                    title: 'Windows',
                    subtitle: 'Windows 10 and 11',
                    icon: 'mdi-microsoft-windows',
                    color: '#0078d4',
                    downloads: [
                        { label: 'Download for Windows', path: '/api/agent/download' },
                    ],
                },
                {
                    key: 'mac',
                    os: 'mac',
                    title: 'macOS',
                    subtitle: 'Apple Silicon and Intel Macs',
                    icon: 'mdi-apple',
                    color: '#555555',
                    downloads: [
                        { label: 'Apple Silicon (M1, M2, M3, M4)', path: '/api/agent/download/mac' },
                        { label: 'Intel Mac', path: '/api/agent/download/mac-intel' },
                    ],
                    hint: 'Not sure which one? Click the Apple menu > About This Mac. If it shows "Chip", choose Apple Silicon. If it shows "Processor", choose Intel.',
                },
            ],
            steps: [
                { title: 'Download', text: 'Pick the installer for your computer above.' },
                { title: 'Install', text: 'Run the downloaded file and follow the prompts.' },
                { title: 'Sign in', text: 'Open DGTasks and sign in with your usual DGTasks account.' },
            ],
        };
    },
    computed: {
        apiUrl() {
            return (process.env.VUE_APP_API_URL || '').replace(/\/$/, '');
        },
        detectedPlatform() {
            const ua = (navigator.userAgent || '').toLowerCase();
            if (ua.includes('windows')) return 'windows';
            if (ua.includes('mac')) return 'mac';
            return null;
        },
    },
    methods: {
        downloadUrl(path) {
            return `${this.apiUrl}${path}`;
        },
    },
};
</script>

<style scoped>
.download-app {
    max-width: 960px;
    padding-left: 24px;
    padding-right: 24px;
}

.hero {
    position: relative;
    overflow: hidden;
    border-radius: 16px;
    background: linear-gradient(135deg, #1565c0 0%, #0d9488 100%);
    box-shadow: 0 8px 24px rgba(21, 101, 192, 0.25);
}

.hero::after {
    content: '';
    position: absolute;
    right: -60px;
    top: -60px;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
}

.hero-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    margin-bottom: 16px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.18);
}

.hero-sub {
    max-width: 620px;
    color: rgba(255, 255, 255, 0.9);
}

.platform-card {
    position: relative;
    border-radius: 14px;
    transition: box-shadow 0.15s ease, transform 0.15s ease;
}

.platform-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1) !important;
}

.platform-card.recommended {
    border: 2px solid var(--v-primary-base) !important;
}

.recommended-chip {
    position: absolute;
    top: 12px;
    right: 12px;
}

.platform-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 60px;
    border-radius: 14px;
}

.hint {
    border-radius: 8px;
    background: rgba(128, 128, 128, 0.1);
}

.step-number {
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    font-size: 14px;
    font-weight: 700;
    color: #fff;
    background: var(--v-primary-base);
}
</style>
